import type { CanvasTarget } from '../types';
import type { LibraryOpenTarget } from '../artifacts/view-models';
import { savedArtifactCanvasTarget } from '../artifacts/routes';
import { artifactApi } from '../artifacts/api';
import type { WorkspaceFindHit } from '../types';

/** Carries the complete original Library tuple. Incomplete/invalid identity
 * refuses; the saved canvas still checks current version disclosure. */
type LibraryCanvasMapping = (target: LibraryOpenTarget) => CanvasTarget | null;

/** The ONE host seam for opening a Library row. Returns whether a canvas opened. */
export function openLibraryTarget(
  ws: { openCanvas: (canvas: CanvasTarget | null) => boolean },
  target: LibraryOpenTarget,
  map: LibraryCanvasMapping = savedArtifactCanvasTarget,
): boolean {
  const canvas = map(target);
  return canvas === null ? false : ws.openCanvas(canvas);
}

/** Results discovery includes canonical saved work. Resolve its actual named
 * version through the existing current-authorized reader, never as a legacy Result.
 * Library and Search both open their Result hits through here. */
export async function workspaceResultCanvasTarget(
  hit: Pick<WorkspaceFindHit, 'id' | 'metadata'>,
): Promise<CanvasTarget | null> {
  if (!Object.hasOwn(hit.metadata ?? {}, 'savedArtifact')) return { kind: 'result', id: hit.id };
  const selection = hit.metadata?.savedArtifact as {
    artifactId?: unknown;
    purpose?: unknown;
  } | null;
  if (
    !selection ||
    selection.artifactId !== hit.id ||
    (selection.purpose !== 'editor' && selection.purpose !== 'viewer')
  )
    return null;
  const history = await artifactApi.readHistory({ artifactId: hit.id, purpose: selection.purpose });
  if (
    history.artifactId !== hit.id ||
    history.purpose !== selection.purpose ||
    history.load !== 'ready'
  )
    return null;
  const version =
    history.versions.find((row) => row.current) ??
    (selection.purpose === 'viewer' ? history.versions.find((row) => row.published) : undefined);
  return version
    ? savedArtifactCanvasTarget({
        artifactId: history.artifactId,
        versionId: version.versionId,
        revisionId: version.revisionId,
        versionNumber: version.number,
        purpose: history.purpose,
      })
    : null;
}
